// MUI imports
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// React imports
import { useRef, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

// 3rd party dependancy imports
import { useFormik } from 'formik';
import * as yup from 'yup';

import { useLegacyLogin, useLogin } from '../../api/reactQueryHook/useAuth';
import ToggleIcon from '../../components/ToggleIcon';
import { VisibilityOffRounded, VisibilityRounded } from '@mui/icons-material';
import LoginShell, { LOGIN_TOKENS } from '../../components/login/LoginShell';
import LoginIntroAnimation from '../../components/login/LoginIntroAnimation';
import { darkTheme } from '../../utils/customTheme';

// Type Definitions
export type LoginStepType = 'initial' | 'refresh' | 'mfa';

const t = LOGIN_TOKENS;

// Dark text-field styling matching the mockup card inputs.
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: t.bgCard2,
    borderRadius: '6px',
    '& fieldset': { borderColor: t.borderStrong },
    '&:hover fieldset': { borderColor: 'rgba(70,160,235,0.45)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(70,160,235,0.45)' },
  },
  '& .MuiOutlinedInput-input': { color: t.textHigh, fontSize: '13px', padding: '13px 14px' },
  '& .MuiOutlinedInput-input::placeholder': { color: t.textLow, opacity: 1 },
} as const;

function Login() {
  // navigate hook to enable navigation in the app
  const navigate: NavigateFunction = useNavigate();

  // navigate function to forgot password page
  const handlForgotPasswordNavigation = () => navigate('/forgot-password');

  // --- Intro animation orchestration ---
  // revealed → app chrome (sidebar + card) slides in; introDone → stage removed / agents static.
  const [revealed, setRevealed] = useState<boolean>(false);
  const [introDone, setIntroDone] = useState<boolean>(false);
  const rollerItemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const registerRollerItem = (i: number, el: HTMLDivElement | null) => { rollerItemsRef.current[i] = el; };
  const skipIntro = () => { setRevealed(true); setIntroDone(true); };

  // --------------------------------------------------------**********************************************------------------------------------------------------------
  // Preliminary Login Flow
  // state to track & conditionally render the login steps
  const [loginStep, setLoginStep] = useState<LoginStepType>('initial');

  // statet to track and manage th epassword visibility setting
  const [showPassword, setShowPassword] = useState<boolean>(false);

  //  function to alter the pasword visibility
  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  // state to decide whether to show qr code or not
  const [showQR, setShowQR] = useState<boolean>(false);

  // function to toggle qr code visibility
  const toggleQRCodeVisibility = () => setShowQR(prev => !prev);

  // -- state to track the refresh password
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [refreshPassword, setRefreshPassword] = useState<boolean>(false);

  // -- state to track the MFA
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mfa, setMFA] = useState<boolean>(false);

  // -- state to track the MFA skip is allowed or not
  const [mfaSkip, setMFASkip] = useState<boolean>(false);

  // -- state to track the MFA code
  const [mfaQRCode, setMFAQRCode] = useState<string>('');

  // -- state to track the MFA cooldown period
  const [mfaCooldownPeriod, setMFACooldownPeriod] = useState<number | null>(null);

  // -- state to track the MFA code
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mfaSecret, setMFASecret] = useState<string | null>(null);

  // -- state to track the preliminary login loading
  const [preliminaryLoading, setPreliminaryLoading] = useState<boolean>(false);

  // -- state to track the central email ID
  const [centralEmailID, setCentralEmailID] = useState<string>('');

  // -- state to track the central password
  const [centralPassword, setCentralPassword] = useState<string>('');

  // -- function to cancel/abort opration and reload
  function abortAndReload() {
    window.location.reload();
  }

  // React Query API Initialization
  // -- Legacy login initailization
  const { mutate: legacy_login_mutation } = useLegacyLogin();

  // -- MFA login initialization
  const { mutate: mfa_login_mutation, isPending: mfa_login_loading } = useLogin();

  // Input tracking for legacy login
  // -- Legacy login schema
  const legacyLoginSchema = yup.object({
    email_id: yup.string().email().required(),
    password: yup.string().required(),
  });

  // -- Legacy login formik
  const legacyLogin = useFormik({
    initialValues: {
      email_id: '',
      password: '',
    },
    validationSchema: legacyLoginSchema,
    onSubmit: values => {
      legacy_login_mutation({
        email_id: values.email_id,
        password: values.password,
        setPreliminaryLoading,
        setCentralEmailID,
        setCentralPassword,
        setRefreshPassword,
        setMFA,
        setMFASkip,
        setMFAQRCode,
        setMFACooldownPeriod,
        setMFASecret,
        setLoginStep,
        mfa_login_mutation,
      });
    },
  });

  // Input tracking for MFA login
  // -- MFA login schema
  const mfaLoginSchema = yup.object({
    mfa_code: yup.string().required(),
  });

  // -- MFA login formik
  const mfaLogin = useFormik({
    initialValues: {
      mfa_code: '',
    },
    validationSchema: mfaLoginSchema,
    onSubmit: values => {
      handle_mfa_login_mutation(false, values);
    },
  });

  // -- function to call the mfa login mutation
  const handle_mfa_login_mutation = (
    skip: boolean,
    values: { mfa_code: string }
  ) => {
    mfa_login_mutation({
      email_id: centralEmailID,
      password: centralPassword,
      mfa_skipped: skip,
      mfa_code: values.mfa_code,
      login_type: 'legacy',
      setPreliminaryLoading,
    });
  };

  return (
    <ThemeProvider theme={darkTheme}>
      {!introDone && (
        <>
          <LoginIntroAnimation
            getRollerItems={() => rollerItemsRef.current}
            onReveal={() => setRevealed(true)}
            onComplete={() => setIntroDone(true)}
          />
          <Box
            component="button"
            onClick={skipIntro}
            sx={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 100, cursor: 'pointer',
              fontFamily: "'Public Sans', sans-serif", fontSize: '9px', fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase', color: t.textLow,
              background: 'none', border: `1px solid ${t.border}`, borderRadius: '6px', padding: '8px 14px',
              '&:hover': { color: t.textMed, borderColor: t.borderStrong },
            }}
          >
            Skip intro
          </Box>
        </>
      )}

      <LoginShell revealed={revealed} instantAgents={introDone} registerRollerItem={registerRollerItem}>
        {/* Login card — fades up once the chrome is revealed */}
        <Box
          sx={{
            width: '100%', maxWidth: 460,
            background: t.bgCard, border: `1px solid ${t.borderStrong}`, borderRadius: '16px',
            padding: '52px 48px 44px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
            opacity: revealed ? 1 : 0, transform: revealed ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 600ms ease 400ms, transform 700ms cubic-bezier(0.34,1.1,0.64,1) 400ms',
          }}
        >
          {loginStep === 'initial' && (
            <>
              <Typography sx={{ fontSize: '27px', fontWeight: 200, color: t.textHigh, mb: 3, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                <Box component="span" sx={{ fontWeight: 800 }}>Sign in</Box> to TheBPR.ai
              </Typography>

              <Box component="form" onSubmit={legacyLogin.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <TextField
                  autoFocus
                  fullWidth
                  autoComplete="username"
                  placeholder="Email"
                  name="email_id"
                  onChange={legacyLogin.handleChange}
                  onBlur={legacyLogin.handleBlur}
                  error={Boolean(legacyLogin.touched.email_id && legacyLogin.errors.email_id)}
                  type="email"
                  disabled={preliminaryLoading}
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  autoComplete="current-password"
                  placeholder="Password"
                  name="password"
                  onChange={legacyLogin.handleChange}
                  onBlur={legacyLogin.handleBlur}
                  error={Boolean(legacyLogin.touched.password && legacyLogin.errors.password)}
                  type={showPassword ? 'text' : 'password'}
                  disabled={preliminaryLoading}
                  sx={fieldSx}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <ToggleIcon
                            on={showPassword}
                            onToggle={togglePasswordVisibility}
                            onIcon={<VisibilityOffRounded />}
                            offIcon={<VisibilityRounded />}
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button
                  type="submit"
                  loading={preliminaryLoading}
                  sx={{
                    mt: 1, py: '15px',
                    background: `linear-gradient(180deg, ${t.primary}, ${t.accent})`,
                    color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', borderRadius: '6px', boxShadow: '0 4px 18px rgba(23,120,199,0.35)',
                    '&:hover': { boxShadow: '0 6px 28px rgba(23,120,199,0.55)', transform: 'translateY(-1px)' },
                  }}
                >
                  Enter TheBPR.ai
                </Button>

                {/* <Box
                  onClick={preliminaryLoading ? undefined : handlForgotPasswordNavigation}
                  sx={{
                    mt: '6px', textAlign: 'center', fontSize: '11px', color: t.accent,
                    cursor: preliminaryLoading ? 'not-allowed' : 'pointer',
                    pointerEvents: preliminaryLoading ? 'none' : 'all',
                  }}
                >
                  Forgot password?
                </Box> */}
              </Box>
            </>
          )}

          {/* MFA step — functional, minimally styled (inherits dark theme) */}
          {loginStep === 'mfa' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Typography variant="h6">{mfaQRCode ? 'Setup MFA' : 'Enter MFA Code'}</Typography>

              <Box component="form" onSubmit={mfaLogin.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <Avatar>1</Avatar>
                  <Typography variant="body2">
                    {mfaQRCode
                      ? 'Install any Authenticator app, such as Google Authenticator, Microsoft Authenticator or Authy.'
                      : 'Open the Authenticator app you have setup.'}
                  </Typography>
                </Box>

                {mfaQRCode && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <Avatar>2</Avatar>
                    <Button onClick={toggleQRCodeVisibility}>{showQR ? 'Hide QR' : 'Show QR'}</Button>
                    {showQR ? (
                      <Box component="img" src={mfaQRCode} alt="MFA QR Code" sx={{ width: '100px', height: '100px' }} />
                    ) : (
                      <Typography variant="body2">
                        Click on Show QR and open the Authenticator app to scan the QR Code.
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <Avatar>{mfaQRCode ? '3' : '2'}</Avatar>
                  <TextField
                    autoFocus
                    fullWidth
                    autoComplete="one-time-code"
                    placeholder="MFA Security Code"
                    name="mfa_code"
                    onChange={mfaLogin.handleChange}
                    onBlur={mfaLogin.handleBlur}
                    error={Boolean(mfaLogin.touched.mfa_code && mfaLogin.errors.mfa_code)}
                    disabled={mfa_login_loading}
                    sx={fieldSx}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button type="submit" variant="contained" loading={mfa_login_loading}>
                    Verify
                  </Button>
                </Box>

                <Box>
                  {mfaSkip && (
                    <Divider sx={{ width: '100%' }}>
                      <Typography variant="caption">Or</Typography>
                    </Divider>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
                    <Button variant="text" disabled={mfa_login_loading} onClick={abortAndReload}>
                      Back
                    </Button>
                    {mfaSkip && (
                      <Box>
                        <Button
                          variant="text"
                          disabled={mfa_login_loading}
                          onClick={() => handle_mfa_login_mutation(true, { mfa_code: '' })}
                        >
                          Skip
                        </Button>
                        <Tooltip
                          title={`${mfaCooldownPeriod === 0 ? 7 : 7 - mfaCooldownPeriod!} day(s) left, until you can access the app without configuring MFA`}
                          arrow
                          enterTouchDelay={0}
                        >
                          <IconButton size="small">
                            <InfoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </LoginShell>
    </ThemeProvider>
  );
}

export default Login;
